import {
    Button, Card, CardContent, CardHeader, CircularProgress, Divider, Grid, Typography, Box, Dialog, DialogTitle, DialogContent
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import axios from 'src/utils/axios';
import { getUser } from 'src/utils/Common';
import ErrorMessage from 'src/components/ErrorMessage';
import TechGPTSVGIcon from 'src/assets/images/chat-logo-techgpt-small.svg';

const useStyles = makeStyles((theme) => ({
    root: {},
    contentB: {
        minHeight: 200,
    },
    dividerB: {
        backgroundColor: '#1D3343'
    },
    suggestionText: {
        padding: theme.spacing(2),
        backgroundColor: '#f5f5f5',
        borderRadius: theme.spacing(1),
        marginTop: theme.spacing(2),
        lineHeight: 1.8,
        '& h1, & h2, & h3, & h4, & h5, & h6': {
            fontWeight: 600,
            color: '#1D3343',
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(1),
            fontSize: '14px',
        },
        '& p': {
            marginBottom: theme.spacing(1),
        },
        '& ul, & ol': {
            paddingLeft: theme.spacing(3),
            marginBottom: theme.spacing(1),
        },
        '& li': {
            marginBottom: theme.spacing(0.5),
        },
        '& strong': {
            fontWeight: 600,
        },
    },
    techGptButton: {
        marginTop: theme.spacing(2),
        textTransform: 'none',
        fontSize: '1rem',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: '#fff',
    },
    techGptLogo: {
        marginLeft: theme.spacing(1),
        width: 40,
        height: 40,
    }
}));

function AiSuggestion({
    className, WorkOrderId, readonly
}) {
    const classes = useStyles();
    const { t } = useTranslation('work_order');
    const [suggestionData, setSuggestionData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isTechGptOpen, setIsTechGptOpen] = useState(false);
    const [isSpecialTechGptOpen, setIsSpecialTechGptOpen] = useState(false);
    const SessionUser = getUser();
    const isOnline = useSelector((state) => state.status?.isOnline);

    const handleOpenTechGpt = () => {
        setIsTechGptOpen(true);
        window.dispatchEvent(new Event('toggleTechGpt'));
    };

    const handleOpenSpecialTechGpt = () => {
        setIsSpecialTechGptOpen(true);
    };

    useEffect(() => {
        let mounted = true;

        const fetchAISuggestion = () => {
            if (!WorkOrderId) {
                setLoading(false);
                return;
            }

            axios.get(`/GetAiSuggestion`, {
                params: {
                    workOrderId: WorkOrderId
                }
            })
                .then((response) => {
                    if (mounted) {
                        setSuggestionData(response.data);
                        setLoading(false);
                    }
                })
                .catch((error) => {
                    console.error('Error fetching AI suggestion:', error);
                    setLoading(false);
                });
        };

        fetchAISuggestion();

        return () => {
            mounted = false;
        };
    }, [WorkOrderId]);

    const shouldDisplaySuggestion = () => {
        return suggestionData && suggestionData.Suggestion;
    };

    const isPending = () => {
        return suggestionData && suggestionData.StatusId === 1;
    };

    const isCompleted = () => {
        return suggestionData && suggestionData.StatusId === 2 && suggestionData.IsSuccessMessage === true;
    };

    const parseMarkdown = (text) => {
        if (!text) return '';
        return text.replace(/\\n\\n/g, '\n\n').replace(/\\n/g, '\n');
    };

    if (!isOnline) {
        return (
            <Card>
                <CardHeader title={t('lbl_AI_Oneri')} />
                <Divider />
                <CardContent className={classes.contentB}>
                    <ErrorMessage message={t('Offline_Message')} />
                </CardContent>
            </Card>
        );
    }

    return (
        <div>
            <Card className={clsx(classes.root, className)}>
                <CardHeader title={t('lbl_AI_Oneri')} />
                <Divider />
                <CardContent className={classes.contentB}>
                    {loading ? (
                        <CircularProgress
                            status="loading"
                            style={{ marginLeft: '50%' }}
                        />
                    ) : isPending() ? (
                        <Typography color="textSecondary" align="center" style={{ padding: 16 }}>
                            {t('lbl_Oneri_Beklemede')}
                        </Typography>
                    ) : isCompleted() ? (
                        <Fragment>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom>
                                        {t('lbl_tavsiye')}
                                    </Typography>
                                    <div className={classes.suggestionText}>
                                        <ReactMarkdown>{parseMarkdown(suggestionData.Suggestion)}</ReactMarkdown>
                                    </div>

                                    <Box display="flex" justifyContent="flex-end" mt={2} mb={3}>
                                        <Button
                                            onClick={handleOpenSpecialTechGpt}
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                        >
                                            {t('lbl_techGpt_devam_et')}
                                            <img src={TechGPTSVGIcon} alt="TechGPT" className={classes.techGptLogo} />
                                        </Button>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}> </Grid>
                                <Grid item xs={12}> </Grid>
                            </Grid>
                        </Fragment>
                    ) : (
                        <Fragment>
                            <Typography color="textSecondary" align="center">
                                {t('lbl_oneri_Mevcut_Degil')}
                            </Typography>
                            <Box display="flex" justifyContent="flex-end" mt={2} mb={1}>
                                <Button
                                    onClick={handleOpenSpecialTechGpt}
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                >
                                    {t('lbl_techGpt_devam_et')}
                                    <img src={TechGPTSVGIcon} alt="TechGPT" className={classes.techGptLogo} />
                                </Button>
                            </Box>
                        </Fragment>
                    )}
                </CardContent>
            </Card>

            <Dialog
                fullWidth
                maxWidth="lg"
                open={isSpecialTechGptOpen}
                onClose={() => setIsSpecialTechGptOpen(false)}
            >
                <DialogTitle>
                    TechGpt - {t('lbl_techGpt_continue_conversation')}
                </DialogTitle>
                <DialogContent style={{ padding: 0, height: '80vh' }}>
                    <iframe
                        src={`https://techgpt.beko.com/iframe?conversationId=${suggestionData?.ConversationId || ''}`}
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        title="TechGpt"
                        style={{ display: 'block' }}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
}

AiSuggestion.propTypes = {
    className: PropTypes.string,
    WorkOrderId: PropTypes.number,
    readonly: PropTypes.bool,
};

AiSuggestion.defaultProps = {
    readonly: true,
};

export default AiSuggestion;
